import React, { FC, useMemo, ChangeEvent, useCallback } from "react";
import { useIntl } from "react-intl";
import { useAppointmentBookingContext } from "../../Appointment/AppointmentBookingContext";
import messages from "./messages";
import CheckboxGroup from "../../Unknown/CheckboxGroup";
import parseAddOnToCheckboxGroupOption from "./parseAddOnToCheckboxGroupOption";
import { calcAddOnPrice } from "./calcAddOnPrice";

const SelectAppointmentAddOnsForm: FC = () => {
  const intl = useIntl();

  const {
    selectedCenter,
    addOns,
    selectedAddOns,
    onAddOnsChange,
    isSubmitLoading,
    selectedCenterServices,
  } = useAppointmentBookingContext();

  const selectedStudiesCount = useMemo(() => {
    return selectedCenterServices.reduce((count, curr) => {
      return count + (curr?.studyIds?.length || 0);
    }, 0);
  }, [selectedCenterServices]);

  const onQuantityChange = useCallback(
    (addOnId: string, quantity: number): void => {
      onAddOnsChange((prev) =>
        prev.map((selected) => {
          if (selected.id !== addOnId) return selected;

          const price = calcAddOnPrice({
            addOnPrice: selected.unitPrice,
            isPerStudyPricing: selected.isPerStudyPricing,
            selectedStudiesCount,
            quantity,
          });

          return { ...selected, quantity, price };
        }),
      );
    },
    [onAddOnsChange, selectedStudiesCount],
  );

  const options = useMemo(() => {
    const { data } = addOns;

    if (!selectedCenter || !data?.length) return [];

    const { defaultCurrencyCode } = selectedCenter;

    return data
      .filter((addOn) => addOn.appliesTo === "appointment")
      .map((addOn) => {
        const selected = selectedAddOns.find(
          (selectedAddOn) => selectedAddOn.id === addOn.id,
        );
        const checked = !!selected;
        const quantity = selected?.quantity ?? 1;

        const price = calcAddOnPrice({
          addOnPrice: addOn.price,
          isPerStudyPricing: addOn.isPerStudyPricing,
          selectedStudiesCount,
          quantity,
        });

        return parseAddOnToCheckboxGroupOption({
          addOn: { ...addOn, price },
          currencyCode: defaultCurrencyCode,
          checked,
          quantity,
          onQuantityChange: addOn.allowMultiplePurchases
            ? (newQuantity: number) => onQuantityChange(addOn.id, newQuantity)
            : undefined,
        });
      });
  }, [
    addOns,
    selectedCenter,
    selectedAddOns,
    selectedStudiesCount,
    onQuantityChange,
  ]);

  const onChecked = useCallback(
    (checkedValue?: string): void => {
      const { data } = addOns;

      if (!selectedCenter || !data?.length) return;

      if (!checkedValue) return;

      const selectedAddOn = data.find((addOn) => addOn.id === checkedValue);

      if (!selectedAddOn) return;

      const price = calcAddOnPrice({
        addOnPrice: selectedAddOn.price,
        isPerStudyPricing: selectedAddOn.isPerStudyPricing,
        selectedStudiesCount,
      });

      const formattedSelectedAddOn = {
        ...selectedAddOn,
        price,
        quantity: 1,
      };

      onAddOnsChange((prev) => [...prev, formattedSelectedAddOn]);
    },
    [addOns, onAddOnsChange, selectedCenter, selectedStudiesCount],
  );

  const onUnchecked = useCallback(
    (uncheckedValue?: string) => {
      if (!uncheckedValue) return;

      const indexOfValue = selectedAddOns.findIndex(
        (addOn) => addOn.id === uncheckedValue,
      );

      const isValueInSelectedItems = indexOfValue !== -1;

      if (!isValueInSelectedItems) return;

      onAddOnsChange((prev) => {
        const newAddOns = [...prev];
        newAddOns.splice(indexOfValue, 1);
        return newAddOns;
      });
    },
    [onAddOnsChange, selectedAddOns],
  );

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>, checked: boolean): void => {
      const actionFunc = checked ? onChecked : onUnchecked;
      actionFunc(e?.target?.value);
    },
    [onChecked, onUnchecked],
  );

  if (!selectedCenter || !addOns.data?.length || !options.length) return null;

  return (
    <CheckboxGroup
      title={intl.formatMessage(messages.title)}
      options={options}
      onChange={onChange}
      disabled={isSubmitLoading}
    />
  );
};

export default SelectAppointmentAddOnsForm;
